import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Dialog } from 'primereact/dialog'
import { StepperRefAttributes } from 'primereact/stepper'

import { useBackgroundTaskStatus } from '../../../../../../../data/batchstatus/hooks'
import { EInvoiceError } from '../../../../../data'

type Props = {
  locationUrl: string
  retry: string
  isDialogVisible: boolean
  setIsDialogVisible: (visible: boolean) => void
  stepperRef: React.MutableRefObject<StepperRefAttributes | null>
  onTaskComplete?: () => void
  setVerificationError?: React.Dispatch<
    React.SetStateAction<EInvoiceError | undefined>
  >
}

export const VerificationDialog: React.FC<Props> = ({
  locationUrl,
  retry,
  isDialogVisible,
  setIsDialogVisible,
  stepperRef,
  onTaskComplete,
  setVerificationError,
}) => {
  const { t } = useTranslation('eInvoiceDetails')

  const { data: backgroundStatus } = useBackgroundTaskStatus<EInvoiceError>(
    locationUrl,
    retry,
  )

  // Handle background task status changes
  useEffect(() => {
    if (!backgroundStatus || !isDialogVisible) return

    // Phase 1 means processing is complete
    if (backgroundStatus.phase === 1) {
      // Set the found errors to verification error state
      if (setVerificationError && backgroundStatus?.errors) {
        setVerificationError(backgroundStatus?.errors)
      }

      // Close dialog
      setIsDialogVisible(false)

      // Reload e-invoice data to get updated validation results
      if (onTaskComplete) {
        onTaskComplete()
      }

      // Navigate to verification step to show results
      stepperRef.current?.nextCallback()
    }
  }, [
    backgroundStatus,
    isDialogVisible,
    stepperRef,
    setIsDialogVisible,
    onTaskComplete,
    setVerificationError,
  ])

  return (
    <Dialog
      visible={isDialogVisible}
      onHide={() => null}
      closable={false}
      closeOnEscape={false}
      dismissableMask={false}
      draggable={false}
      resizable={false}
      blockScroll
      contentClassName="text-center px-8"
    >
      <h3 className="mb-2 mt-0">
        {t('stepper.additionalDocuments.verify.dialog.title')}
      </h3>

      <div className="flex justify-content-center mt-4">
        <i className="pi pi-spin pi-spinner text-blue-500 text-4xl"></i>
      </div>
    </Dialog>
  )
}
